/**
 * eslint-disable react/jsx-props-no-spreading
 *
 * @format
 */

import {
  faComments,
  faEdit,
  faFileInvoice,
  faTruckFast,
} from '@fortawesome/pro-solid-svg-icons';

import {
  faAddressBook,
  faAlarmExclamation,
  faArrowProgress,
  faAt,
  faCalendarAlt,
  faCalendarCheck,
  faCheckDouble,
  faCircleInfo,
  faCopy,
  faEmptySet,
  faEnvelope,
  faEnvelopeCircleCheck,
  faFileSignature,
  faHandshakeSlash,
  faHourglassClock,
  faHourglassStart,
  faIdCard,
  faLocationDot,
  faMessageCheck,
  faMessageSms,
  faPaperPlane,
  faPhone,
  faPhoneArrowUpRight,
  faPhonePlus,
  faPhoneSlash,
  faPhoneVolume,
  faStore,
  faVideo,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Clear } from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import Box from '@mui/material/Box';
import { lightGreen } from '@mui/material/colors';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment/moment';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { TableVirtuoso } from 'react-virtuoso';
import { api, getApiUrl } from '../../../api.ts';
import { selectAuth } from '../../../redux/authSlice';
import { ThemeProvider } from '../../../theme';
import CacheManager from '../../../utils/cache';
import { leadInfo, MEETING_TYPE_CHOICES } from '../../../utils/constants';
import { parseFieldError } from '../../../utils/errors';
import { useCacheInvalidation, usePrevious } from '../../../utils/hooks';
import BoxLoader from '../../misc/BoxLoader';
import CustomAutocomplete from '../../misc/CustomAutocomplete';
import NewTooltip from '../../misc/NewTooltip';
import FollowUpDialog from '../followUp/FollowUpDialog';
import DashboardInlineEventManager, {
  CommunicationBadge,
} from './DashboardInlineEventManager';
import ProvenanceIcon from './DashboardInlineProvenanceIcon';
import DashboardInlineProvenanceInfo from './DashboardInlineProvenanceInfo';
import LeadCommentDialog from './LeadCommentDialog';
import ProjectManagerIcon, { stringAvatar } from './ProjectManagerIcon';

const DashboardContext = createContext(null);
const RowContext = createContext(null);

const RowComponent = forwardRef((props, ref) => {
  const [hover, setHover] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [toBeClosed, setToBeClosed] = useState(false);
  const contextData = useMemo(() => ({ hover, setModalOpened }), [hover]);

  useEffect(() => {
    if (toBeClosed && !modalOpened) {
      setModalOpened(false);
      setToBeClosed(false);
      setHover(false);
    }
  }, [toBeClosed, modalOpened]);

  return (
    <RowContext.Provider value={contextData}>
      <TableRow
        {...props}
        ref={ref}
        onMouseOver={() => setHover(true)}
        onMouseOut={() => (modalOpened ? setToBeClosed(true) : setHover(false))}
        sx={{
          height: 80,
          // bgcolor: bgColorMapping(props.item.current_follow_up_status),
          bgcolor: props.item.project_manager === 1 ? 'error.light' : undefined,
          '&:hover': {
            bgcolor:
              props.item.project_manager === 1
                ? 'error.lighter'
                : 'background.default',
          },
        }}
      />
    </RowContext.Provider>
  );
});
RowComponent.displayName = 'RowComponent';

RowComponent.propTypes = {
  item: PropTypes.shape().isRequired,
};

const TableComponents = {
  Scroller: forwardRef((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table
      {...props}
      style={{ borderCollapse: 'separate', tableLayout: 'fixed' }}
      sx={{
        '& .MuiTableCell-root': {
          fontSize: (theme) => ({
            xs: '0.8rem',
            sm: '0.875rem',
            md: '0.875rem',
            lg: '0.875rem',
            xl: '0.875rem',
          }),
        },
      }}
      size="small"
    />
  ),
  TableHead: (props) => (
    // eslint-disable-next-line react/destructuring-assignment,react/prop-types
    <TableHead {...props} style={{ ...props.style, zIndex: 10 }} />
  ),
  TableRow: RowComponent,
  TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};
TableComponents.Scroller.displayName = 'TableComponentsScroller';
TableComponents.TableBody.displayName = 'TableComponentsTableBody';

const columnsWidth = {
  id: 75,
  provenance: 35,
  status: 260,
  category: 110,
  action_result: 190,
  last_name: 170,
  lost: 80,
  phone: 180,
  project_manager: 65,
  setter: 83,
  zip_code: 60,
  last_offer_kwc: 84,
};

const isStatusActive = (currentSorting, columnName) =>
  [columnName, `-${columnName}`].includes(currentSorting);

const getSortingDirection = (currentSorting, columnName) => {
  if (isStatusActive(currentSorting, columnName)) {
    return currentSorting.startsWith('-') ? 'desc' : 'asc';
  }
  return undefined;
};

const FixedHeaderContent = React.memo(() => {
  const { sorting, handleSortingChange } = useContext(DashboardContext);
  return (
    <TableRow
      sx={{
        bgcolor: 'background.paper',
      }}
    >
      <TableCell sx={{ width: columnsWidth.id, textAlign: 'center' }}>
        <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
          ID
        </Typography>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.provenance }}>
        <NewTooltip content="Provenance">
          <Typography
            noWrap
            sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
          />
        </NewTooltip>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.status, textAlign: 'center' }}>
        <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
          Statut
        </Typography>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.last_name, paddingLeft: '25px' }}>
        <TableSortLabel
          active={isStatusActive(sorting, 'last_name')}
          direction={getSortingDirection(sorting, 'last_name')}
          onClick={() => handleSortingChange('last_name')}
        >
          <Typography
            noWrap
            sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
          >
            Nom & Prénom
          </Typography>
        </TableSortLabel>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.phone, paddingLeft: '30px' }}>
        <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
          Téléphone
        </Typography>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.project_manager }}>
        <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
          CGP
        </Typography>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.setter }}>
        <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
          Setteur
        </Typography>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.city }}>
        <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
          Ville
        </Typography>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.zip_code, textAlign: 'center' }}>
        <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
          Dpt
        </Typography>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.category, textAlign: 'center' }}>
        <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
          Étape
        </Typography>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.action_result }}>
        <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
          Action & résultat
        </Typography>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.delay }}>
        <NewTooltip content="Retard sur le funnel de vente">
          <Typography
            noWrap
            sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
          >
            Retard
          </Typography>
        </NewTooltip>
      </TableCell>
    </TableRow>
  );
});
FixedHeaderContent.displayName = 'FixedHeaderContent';

function fixedHeaderContent(index) {
  return <FixedHeaderContent index={index} />;
}

const copyLeadInfo = (lead, enqueueSnackbar) => {
  if ('clipboard' in navigator) {
    const information = leadInfo(lead);
    navigator.clipboard
      .writeText(information)
      .then(() => {
        enqueueSnackbar('Adresse copiée !', { variant: 'success' });
      })
      .catch((err) => {
        console.error(err);
        const { message } = parseFieldError(err);
        if (message) enqueueSnackbar(message, { variant: 'error' });
      });
  }
};

const isDueDateReached = (dueDate) => {
  const currentDate = new Date();
  const dueDateObj = new Date(dueDate);
  return dueDateObj >= currentDate;
};

const getDaysOfDelay = (dueDate) => {
  const currentDate = moment().startOf('day');
  const dueDateMoment = moment(dueDate).startOf('day');
  return Math.abs(dueDateMoment.diff(currentDate, 'days'));
};

const filterLeadsByScheduledOrDueDate = (leads) => {
  const today = new Date();

  return leads.filter((lead) => {
    if (
      lead.current_follow_up_status &&
      lead.current_follow_up_status.is_contact
    ) {
      return true;
    }
    const scheduledDate = lead.follow_up_meeting?.scheduled_date
      ? new Date(lead.follow_up_meeting.scheduled_date)
      : null;
    const dueDate = lead.follow_up_task?.due_date
      ? new Date(lead.follow_up_task.due_date)
      : null;

    if (scheduledDate && scheduledDate > today) {
      return false;
    }

    return (
      (scheduledDate && scheduledDate <= today) || (dueDate && dueDate <= today)
    );
  });
};

function ItemContent({ index, lead }) {
  const { onLeadChange } = useContext(DashboardContext);
  const { hover, setModalOpened } = useContext(RowContext);
  const [showInlineEventActions, setShowInlineEventActions] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [isFollowUpDialogOpen, setIsFollowUpDialogOpen] = useState(false);
  const [followUpDialogSection, setFollowUpDialogSection] = useState(null);

  const navigate = useNavigate();

  const confirmTechnicalVisitNoR3Set = () => {
    // setLoading(true);
    const data = {
      lead: lead.id,
      is_confirmed: true,
      has_planned_next_meeting: false,
      has_signed: false,
    };
    api
      .post(`/follow-up/${lead.id}/confirm_meeting/`, {}, data)
      .then(() => {
        // setLoading(false);
        // handleClose();
        if (onLeadChange) {
          onLeadChange(lead);
        }
        enqueueSnackbar('Les informations ont été mises à jour', {
          variant: 'success',
        });
      })
      .catch((err) => {
        const { state, message } = parseFieldError(err);
        enqueueSnackbar(
          message ?? 'Impossible de mettre à jour les informations',
          {
            variant: 'error',
          },
        );
        // setLoading(false);
      });
  };

  const signContract = () => {
    api
      .post(`/follow-up/${lead.id}/sign_contract/`, {}, {})
      .then(() => {
        navigate(`/lead/${lead.id}/quote`);
        enqueueSnackbar('Les informations ont été mises à jour', {
          variant: 'success',
        });
      })
      .catch((err) => {
        const { state, message } = parseFieldError(err);
        enqueueSnackbar(
          message ?? 'Impossible de mettre à jour les informations',
          {
            variant: 'error',
          },
        );
      });
  };

  const getFollowUpStatusIcon = (status) => {
    if (status.is_contact) return faAddressBook;
    if (status.is_follow_up_r1) return faPhone;
    if (status.is_follow_up_r2) return faPhonePlus;
    if (status.is_follow_up_r3) return faHandshakeSlash;
    if (status.is_r1_completed) return faTruckFast;
    if (status.is_confirmation_r1) return faCalendarCheck;
    if (status.is_confirmation_r2) return faCalendarCheck;
    if (status.is_confirmation_technical_visit) return faCalendarCheck;
    if (status.is_confirmation_r3) return faCalendarCheck;
    return null;
  };

  const iconColorMapping = (item) => {
    if (item.is_contact) return 'grey';
    if (item.is_follow_up_r1) return 'purple';
    if (item.is_confirmation_r1) return 'darkpurple';
    if (item.is_follow_up_r2) return 'orange';
    if (item.is_confirmation_r2) return 'darkorange';
    if (item.is_follow_up_r3) return 'brown';
    if (item.is_confirmation_r3) return 'brown';
    // if (item.is_follow_up_r3) return 'green';
    // if (item.is_confirmation_r3) return 'darkgreen';
    if (item.is_r1_completed) return 'green';
    if (item.is_confirmation_technical_visit) return 'darkgreen';
    return undefined;
  };

  const actionMapping = {
    1: 'Prendre RDV',
    2: 'Signer ou prendre RDV',
    3: 'Appel',
  };

  const getAction = (item) => {
    if (item.current_follow_up_status.is_r1_completed) {
      return 1;
    }
    if (
      (item.current_follow_up_status.is_confirmation_technical_visit ||
        item.current_follow_up_status.is_follow_up_r3 ||
        item.current_follow_up_status.is_confirmation_r3) &&
      item.follow_up_task.is_meeting_confirmation
    ) {
      return 2;
    }
    return 3;
  };

  // const actionResultMapping = {
  //   // TODO: update values
  //   1: 'Planifier RDV téléphonique', // Agenda
  //   2: 'Planifier VT', // Agenda VT
  //   3: 'Saisir les qualifications du lead', // Devis & études
  //   4: 'Pas de réponse', // Pas de réponse
  //   5: 'Contrat signé', // Contrat signé
  //   6: 'Contrat non signé et pas de R3 planifié à la VT', // Pas de R3 planifié
  // };

  const actionResultMapping = {
    // 1: 'Planifier RDV téléphonique',
    1: () => {
      if (lead.current_follow_up_status.is_contact) {
        switch (lead.follow_up_call.call_number) {
          case 1:
            return 'Vous avez le client au téléphone lors du 1er appel, vous planifiez le R1 sur votre agenda';
          case 2:
            return 'Vous avez le client au téléphone lors du 2ème appel, vous planifiez le R1 sur votre agenda';
          case 3:
            return 'Vous avez le client au téléphone lors du 3ème appel, vous planifiez le R1 sur votre agenda';
          case 4:
            return 'Vous avez le client au téléphone lors du 4ème appel, vous planifiez le R1 sur votre agenda';
          case 5:
            return 'Vous avez le client au téléphone lors du 5ème appel, vous planifiez le R1 sur votre agenda';
          default:
            return 'Planifier RDV téléphonique';
        }
      } else if (
        lead.current_follow_up_status.is_follow_up_r1 ||
        lead.current_follow_up_status.is_confirmation_r1
      ) {
        switch (lead.follow_up_call.call_number) {
          case 1:
            return 'Vous avez le client au téléphone lors du 1er appel, vous planifiez le R2 sur votre agenda';
          case 2:
            return 'Vous avez le client au téléphone lors du 2ème appel, vous planifiez le R2 sur votre agenda';
          case 3:
            return 'Vous avez le client au téléphone lors du 3ème appel, vous planifiez le R2 sur votre agenda';
          case 4:
            return 'Vous avez le client au téléphone lors du 4ème appel, vous planifiez le R2 sur votre agenda';
          case 5:
            return 'Vous avez le client au téléphone lors du 5ème appel, vous planifiez le R2 sur votre agenda';
          default:
            return 'Planifier RDV téléphonique';
        }
      } else if (
        lead.current_follow_up_status.is_follow_up_r2 ||
        lead.current_follow_up_status.is_confirmation_r2
      ) {
        switch (lead.follow_up_call.call_number) {
          case 1:
            return 'Vous avez le client au téléphone lors du 1er appel et ne complétez pas le RDV, vous planifiez un nouveau R2 sur votre agenda';
          case 2:
            return 'Vous avez le client au téléphone lors du 2ème appel et ne complétez pas le RDV, vous planifiez un nouveau R2 sur votre agenda';
          case 3:
            return 'Vous avez le client au téléphone lors du 3ème appel et ne complétez pas le RDV, vous planifiez un nouveau R2 sur votre agenda';
          case 4:
            return 'Vous avez le client au téléphone lors du 4ème appel et ne complétez pas le RDV, vous planifiez un nouveau R2 sur votre agenda';
          case 5:
            return 'Vous avez le client au téléphone lors du 5ème appel et ne complétez pas le RDV, vous planifiez un nouveau R2 sur votre agenda';
          default:
            return 'Planifier RDV téléphonique';
        }
      } else if (
        lead.current_follow_up_status.is_follow_up_r3 ||
        lead.current_follow_up_status.is_confirmation_r3
      ) {
        switch (lead.follow_up_call.call_number) {
          case 1:
            return 'Vous avez le client au téléphone lors du 1er appel, vous planifiez un nouveau R3 sur votre agenda';
          case 2:
            return 'Vous avez le client au téléphone lors du 2ème appel, vous planifiez un nouveau R3 sur votre agenda';
          case 3:
            return 'Vous avez le client au téléphone lors du 3ème appel, vous planifiez un nouveau R3 sur votre agenda';
          case 4:
            return 'Vous avez le client au téléphone lors du 4ème appel, vous planifiez un nouveau R3 sur votre agenda';
          case 5:
            return 'Vous avez le client au téléphone lors du 5ème appel, vous planifiez un nouveau R3 sur votre agenda';
          default:
            return 'Planifier RDV téléphonique';
        }
      } else if (lead.current_follow_up_status.is_r1_completed) {
        return 'Vous avez généré une offre, planifiez le R2 en cliquant sur ce bouton';
      } else if (
        lead.current_follow_up_status.is_confirmation_technical_visit
      ) {
        return "Vous avez effectué la VT mais n'avez pas signé le contrat, vous devez planifier le R3 sur votre agenda";
      }
      return 'Planifier RDV téléphonique';
    },
    // 2: 'Planifier VT',
    2: () => {
      if (lead.current_follow_up_status.is_r1_completed) {
        return 'Vous avez généré une offre, planifiez la VT en cliquant sur ce bouton';
      }
      if (
        lead.current_follow_up_status.is_confirmation_r2 ||
        lead.current_follow_up_status.is_follow_up_r2 ||
        lead.current_follow_up_status.is_confirmation_r1 ||
        lead.current_follow_up_status.is_follow_up_r1
      ) {
        switch (lead.follow_up_call.call_number) {
          case 1:
            return 'Vous avez le client au téléphone lors du 1er appel, vous planifiez une VT sur votre agenda';
          case 2:
            return 'Vous avez le client au téléphone lors du 2ème appel, vous planifiez une VT sur votre agenda';
          case 3:
            return 'Vous avez le client au téléphone lors du 3ème appel, vous planifiez une VT sur votre agenda';
          case 4:
            return 'Vous avez le client au téléphone lors du 4ème appel, vous planifiez une VT sur votre agenda';
          case 5:
            return 'Vous avez le client au téléphone lors du 5ème appel, vous planifiez une VT sur votre agenda';
          default:
            return 'Planifier RDV téléphonique';
        }
      }
      return 'Planifier VT';
    },
    // 3: 'Saisir les qualifications du lead',
    3: 'Vous êtes au téléphone avec le client et vous effectuez le R1 de suite',
    //   4: 'Pas de réponse', // Pas de réponse
    4: () => {
      if (
        (lead.current_follow_up_status.is_contact &&
          lead.follow_up_call.call_number !== 1) ||
        lead.current_follow_up_status.is_follow_up_r1 ||
        lead.current_follow_up_status.is_confirmation_r1 ||
        lead.current_follow_up_status.is_follow_up_r2 ||
        lead.current_follow_up_status.is_confirmation_r2 ||
        lead.current_follow_up_status.is_follow_up_r3 ||
        lead.current_follow_up_status.is_confirmation_r3
      ) {
        switch (lead.follow_up_call.call_number) {
          case 1:
            return (
              "Vous n'avez pas réussi à avoir le client lors du 1er appel, vous devez lui laisser un message " +
              "sur son répondeur et cliquer sur ce bouton afin d'envoyer un e-mail"
            );
          case 2:
            return (
              "Vous n'avez pas réussi à avoir le client lors du 2ème appel, vous devez lui laisser un message" +
              " sur son répondeur et cliquer sur ce bouton afin d'envoyer un SMS"
            );
          case 3:
            return (
              "Vous n'avez pas réussi à avoir le client lors du 3ème appel, vous devez lui laisser un message " +
              "sur son répondeur et cliquer sur ce bouton afin d'envoyer un SMS"
            );
          case 4:
            return (
              "Vous n'avez pas réussi à avoir le client lors du 4ème appel, signalez cet appel manqué sans envoyer " +
              'de message en cliquant sur ce bouton'
            );
          case 5:
            return (
              "Vous n'avez pas réussi à avoir le client lors du 5ème appel, vous devez lui laisser un message sur son " +
              "répondeur et cliquer sur ce bouton afin d'envoyer un e-mail et de sortir ce client du funnel de vente pour le placer " +
              'dans le programme marketing'
            );
          default:
            return 'Pas de réponse';
        }
      }
      if (lead.current_follow_up_status.is_contact) {
        switch (lead.follow_up_call.call_number) {
          case 1:
            return (
              "Vous n'avez pas réussi à avoir le client lors du 1er appel, vous devez lui laisser un message " +
              "sur son répondeur et cliquer sur ce bouton afin d'envoyer un e-mail et un SMS"
            );
          default:
            return 'Pas de réponse';
        }
      }
      return 'Pas de réponse';
    },
    // 5: 'Contrat signé',
    5: 'Vous avez signé le contrat avec le client, cliquez sur ce bouton pour le sortir du processus de relance',
  };

  const getActionResultTooltip = (result) => {
    const tooltipContent = actionResultMapping[result];
    if (typeof tooltipContent === 'function') {
      return tooltipContent();
    }
    return tooltipContent;
  };

  const actionResultIcons = {
    1: faCalendarAlt,
    2: faTruckFast,
    // 2: faCalendarLinesPen,
    3: faIdCard,
    4: faPhoneSlash,
    5: faFileSignature,
  };

  const actionResultColors = {
    1: 'info',
    // 1: deepOrange[300],
    2: 'primary',
    // 2: 'secondary',
    3: 'secondary',
    // 3: teal[500],
    4: 'warning',
    // 5: 'warning',
    5: lightGreen[500],
  };

  const isMuiColor = (color) =>
    ['primary', 'secondary', 'warning', 'info', 'error'].includes(color);

  function isFollowUpWithoutMessage() {
    return (
      (lead.current_follow_up_status.is_contact === true &&
        lead.follow_up_call.call_number === 4) ||
      (lead.current_follow_up_status.is_follow_up_r1 === true &&
        lead.follow_up_call.call_number === 4) ||
      (lead.current_follow_up_status.is_follow_up_r2 === true &&
        lead.follow_up_call.call_number === 4) ||
      (lead.current_follow_up_status.is_follow_up_r3 === true &&
        lead.follow_up_call.call_number === 4)
    );
  }

  const updateFollowUpDataVoicemailWithoutMessage = () => {
    const data = {
      lead: lead.id,
      action: 'voicemail',
      result: 'no_response',
    };
    api
      .post(`/follow-up/${lead.id}/call_follow_up/`, {}, data)
      .then(() => {
        if (onLeadChange) {
          onLeadChange(lead);
        }
        enqueueSnackbar('Les informations ont été mise à jour', {
          variant: 'success',
        });
      })
      .catch((err) => {
        const { state, message } = parseFieldError(err);
        enqueueSnackbar(
          message ?? 'Impossible de mettre à jour les informations',
          {
            variant: 'error',
          },
        );
      });
  };

  const actionResultOnClick = {
    1: () => {
      setFollowUpDialogSection('calendar');
      setIsFollowUpDialogOpen(true);
    }, // Agenda
    2: () => {
      setFollowUpDialogSection('technicalVisitCalendar');
      setIsFollowUpDialogOpen(true);
    }, // Agenda VT
    3: () => navigate(`/lead/${lead.id}/qualification`), // Devis & études
    4: () => {
      if (isFollowUpWithoutMessage()) {
        updateFollowUpDataVoicemailWithoutMessage();
      } else {
        setFollowUpDialogSection('message');
        setIsFollowUpDialogOpen(true);
      }
    }, // Pas de réponse
    5: () => signContract(), // Contrat signé
    6: () => confirmTechnicalVisitNoR3Set(), // Pas de R3 planifié
  };

  function getActionResult(item) {
    const results = [];
    const action = getAction(item);

    if (action === 1) {
      results.push(1, 2);
    } else if (action === 2) {
      if (lead.current_follow_up_status.is_confirmation_technical_visit) {
        results.push(5, 1);
      } else if (
        lead.current_follow_up_status.is_confirmation_r3 ||
        lead.current_follow_up_status.is_follow_up_r3
      ) {
        results.push(5, 1, 4);
      }
    } else if (action === 3) {
      if (lead.current_follow_up_status.is_contact) {
        results.push(1, 3, 4);
      } else if (
        lead.current_follow_up_status.is_follow_up_r1 ||
        lead.current_follow_up_status.is_confirmation_r1
      ) {
        results.push(1, 2, 3, 4);
      } else if (
        lead.current_follow_up_status.is_follow_up_r2 ||
        lead.current_follow_up_status.is_confirmation_r2
      ) {
        results.push(1, 2, 4);
      } else if (
        lead.current_follow_up_status.is_follow_up_r3 ||
        lead.current_follow_up_status.is_confirmation_r3
      ) {
        results.push(5, 1, 4);
      }
    }

    return results;
  }

  useEffect(() => {
    if (!hover) {
      setShowInlineEventActions(false);
    }
  }, [hover]);

  // const handleIconClick = () => {
  //   setIsFollowUpDialogOpen(true);
  // };
  //
  // const isConfirmationStatus =
  //   lead.current_follow_up_status.is_confirmation_r1 ||
  //   lead.current_follow_up_status.is_confirmation_r2 ||
  //   lead.current_follow_up_status.is_confirmation_r3;

  return (
    <>
      <TableCell sx={{ width: columnsWidth.id, textAlign: 'center' }}>
        <Typography noWrap={!hover} sx={{ fontSize: 'inherit' }}>
          {lead.id}
        </Typography>
      </TableCell>
      <TableCell
        sx={{ width: columnsWidth.provenance, textAlign: 'center', px: 0 }}
      >
        <Typography noWrap={!hover} sx={{ fontSize: 'inherit' }}>
          {hover ? (
            <DashboardInlineProvenanceInfo
              lead={lead}
              onChange={onLeadChange}
            />
          ) : (
            <ProvenanceIcon provenanceId={lead.provenance} />
          )}
        </Typography>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.status }}>
        {hover ? (
          <Grid container justifyContent="space-between" direction="column">
            <Grid item xs={12}>
              <Typography
                noWrap
                sx={{
                  fontSize: 'inherit',
                  maxWidth: columnsWidth.status - 32,
                }}
              >
                {CacheManager.instance
                  .getModel('/leads-status/')
                  .get(lead.status).label || lead.status.label}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <DashboardInlineEventManager
                lead={lead}
                onChange={onLeadChange}
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container justifyContent="space-between" direction="column">
            <Grid item xs={12}>
              <Typography
                noWrap
                sx={{
                  fontSize: 'inherit',
                  maxWidth: columnsWidth.status - 32,
                }}
              >
                {CacheManager.instance
                  .getModel('/leads-status/')
                  .get(lead.status).label || lead.status.label}
                <Box sx={{ ml: 0.5, display: 'none' }} component="span">
                  {lead.has_communication && lead.has_communication > 0 ? (
                    <IconButton
                      variant="contained"
                      size="small"
                      color="primary"
                    >
                      <FontAwesomeIcon icon={faCheckDouble} swapOpacity />
                    </IconButton>
                  ) : (
                    <IconButton variant="contained" size="small" color="error">
                      <FontAwesomeIcon icon={faPhoneSlash} swapOpacity />
                    </IconButton>
                  )}
                </Box>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <ButtonGroup variant="contained" color="secondary" fullWidth>
                <CommunicationBadge
                  badgeContent={lead.has_missed_call}
                  color="error"
                  overlap="circular"
                >
                  <Button>
                    <FontAwesomeIcon icon={faPhoneSlash} />
                  </Button>
                </CommunicationBadge>
                <CommunicationBadge
                  badgeContent={lead.has_voicemail}
                  color="error"
                  overlap="circular"
                >
                  <Button>
                    <FontAwesomeIcon icon={faPhoneVolume} />
                  </Button>
                </CommunicationBadge>
                <CommunicationBadge
                  badgeContent={lead.has_text_message_sent}
                  color="error"
                  overlap="circular"
                >
                  <Button>
                    <FontAwesomeIcon icon={faMessageSms} />
                  </Button>
                </CommunicationBadge>
                <CommunicationBadge
                  badgeContent={lead.has_mail_sent}
                  color="error"
                  overlap="circular"
                >
                  <Button>
                    <FontAwesomeIcon icon={faEnvelope} />
                  </Button>
                </CommunicationBadge>
                <CommunicationBadge overlap="circular">
                  <Button
                    sx={{
                      ...(lead.has_tried_communication &&
                      lead.has_tried_communication > 0
                        ? {
                            bgcolor: 'warning.main',
                            '&:hover': {
                              bgcolor: 'warning.dark',
                            },
                          }
                        : {}),
                      ...(lead.has_communication && lead.has_communication > 0
                        ? {
                            bgcolor: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                            },
                          }
                        : {}),
                    }}
                  >
                    <FontAwesomeIcon icon={faMessageCheck} />
                  </Button>
                </CommunicationBadge>
              </ButtonGroup>
            </Grid>
          </Grid>
        )}
      </TableCell>
      <TableCell sx={{ width: columnsWidth.last_name }}>
        <Grid container>
          <Grid item xs={12}>
            <Typography noWrap={!hover} sx={{ fontSize: 'inherit' }}>
              <strong>{lead.last_name.toUpperCase()}</strong> {lead.first_name}
            </Typography>
          </Grid>
          {hover ? (
            <Grid item xs={12}>
              <Grid container spacing={1} justifyContent="flex-start">
                <Grid item>
                  <NewTooltip content="Qualification" placement="top">
                    <IconButton
                      variant="contained"
                      color="primary"
                      size="small"
                      to={`../lead/${lead.id}/qualification`}
                      component={Link}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </IconButton>
                  </NewTooltip>
                </Grid>
                <Grid item>
                  <NewTooltip content="Devis & études" placement="top">
                    <IconButton
                      variant="contained"
                      color="primary"
                      size="small"
                      to={`../lead/${lead.id}/quote`}
                      component={Link}
                    >
                      <FontAwesomeIcon icon={faFileInvoice} />
                    </IconButton>
                  </NewTooltip>
                </Grid>
                <Grid item>
                  <LeadCommentDialog
                    lead={lead}
                    setModalOpened={setModalOpened}
                  />
                </Grid>
                {lead && lead.duplicated?.is_duplicated && (
                  <Grid item>
                    <NewTooltip
                      content={
                        <>
                          Ce lead est probablement un doublon de :
                          <ul>
                            {lead.duplicated.similar_leads.map(
                              (similarLead) => (
                                <li key={similarLead.id}>
                                  {similarLead.id} - {similarLead.first_name}{' '}
                                  {similarLead.last_name}
                                </li>
                              ),
                            )}
                          </ul>
                        </>
                      }
                      placement="top"
                    >
                      <FontAwesomeIcon
                        icon={faCopy}
                        color="#DC143C"
                        size="xl"
                        style={{ marginTop: '4px' }}
                      />
                    </NewTooltip>
                  </Grid>
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Grid container spacing={1} justifyContent="flex-start">
                <Grid item>
                  <IconButton variant="contained" color="primary" size="small">
                    <FontAwesomeIcon icon={faEdit} />
                  </IconButton>
                </Grid>
                <Grid item>
                  <IconButton variant="contained" color="primary" size="small">
                    <FontAwesomeIcon icon={faFileInvoice} />
                  </IconButton>
                </Grid>
                <Grid item>
                  <IconButton variant="contained" color="primary" size="small">
                    <FontAwesomeIcon icon={faComments} />
                  </IconButton>
                </Grid>
                {lead && lead.duplicated?.is_duplicated && (
                  <Grid item>
                    <FontAwesomeIcon
                      icon={faCopy}
                      color="#DC143C"
                      size="xl"
                      style={{ marginTop: '4px' }}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.phone }}>
        <Typography noWrap={!hover} sx={{ fontSize: 'inherit' }}>
          {lead.phone_1 && !lead.phone_1.startsWith('+')
            ? lead.phone_1.replace(
                /(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/,
                '$1 $2 $3 $4 $5',
              )
            : lead.phone_1 || <FontAwesomeIcon icon={faEmptySet} />}
          <NewTooltip
            content={
              <>
                {lead.email}
                <br />
                <Typography
                  sx={{
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    fontStyle: 'italic',
                    color: 'warning.main',
                  }}
                >
                  Cliquez pour copier l&apos;adresse e-mail de ce lead.
                </Typography>
              </>
            }
          >
            <IconButton
              sx={{ ml: 1 }}
              onClick={() => {
                navigator.clipboard.writeText(lead.email);
                enqueueSnackbar('Adresse e-mail copiée !', {
                  variant: 'success',
                });
              }}
              size="small"
              color="primary"
            >
              <FontAwesomeIcon icon={faAt} />
            </IconButton>
          </NewTooltip>
        </Typography>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.project_manager, px: 0 }}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid item>
            <ProjectManagerIcon
              lead={lead}
              onChange={onLeadChange}
              hover={hover}
            />
          </Grid>
        </Grid>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.setter, px: 0 }}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid item>
            <ProjectManagerIcon
              lead={lead}
              onChange={onLeadChange}
              hover={hover}
              isSetter
            />
          </Grid>
        </Grid>
      </TableCell>
      <TableCell sx={{ width: columnsWidth.city }}>
        <Typography noWrap={!hover} sx={{ fontSize: 'inherit' }}>
          <NewTooltip
            content={
              <>
                {lead.address_1 && lead.address_1 !== '' && (
                  <div>{lead.address_1}</div>
                )}
                {lead.address_2 && lead.address_2 !== '' && (
                  <div>{lead.address_2}</div>
                )}
                <div>
                  {lead.zip_code || ''}
                  {lead.zip_code && lead.zip_code !== '' ? ' ' : ''}
                  {lead.city || ''}
                </div>
                <br />
                <Typography
                  sx={{
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    fontStyle: 'italic',
                    color: 'warning.main',
                  }}
                >
                  Cliquez pour copier l&apos;adresse de ce lead.
                </Typography>
              </>
            }
          >
            <Typography
              noWrap
              sx={{ fontSize: 'inherit', cursor: 'pointer' }}
              onClick={() => copyLeadInfo(lead, enqueueSnackbar)}
              role="button"
              component="span"
            >
              {!lead.city ? <FontAwesomeIcon icon={faEmptySet} /> : lead.city}
            </Typography>
          </NewTooltip>
        </Typography>
      </TableCell>
      <TableCell
        sx={{ width: columnsWidth.zip_code, textAlign: 'center', px: 0 }}
      >
        {lead.zip_code}
      </TableCell>
      <TableCell
        sx={{
          width: columnsWidth.category,
          justifyContent: 'center',
          px: 0,
          height: 'inherit',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {lead.current_follow_up_status && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NewTooltip content={lead.current_follow_up_status.name}>
              <Box>
                <FontAwesomeIcon
                  icon={getFollowUpStatusIcon(lead.current_follow_up_status)}
                  size="xl"
                  color={iconColorMapping(lead.current_follow_up_status)}
                />
              </Box>
            </NewTooltip>
            {lead.follow_up_meeting &&
              (lead.current_follow_up_status?.is_r1_completed ? (
                <Box sx={{ ml: 1 }}>R2 ou VT</Box>
              ) : (
                <Box
                  sx={{
                    ml: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {MEETING_TYPE_CHOICES[lead.follow_up_meeting.meeting_type]}
                  {lead.follow_up_meeting.meeting_type !== 'technical_visit' &&
                    `.${lead.follow_up_meeting.meeting_number}`}
                  {['telephone', 'visio', 'physique', 'showroom'].includes(
                    lead.follow_up_meeting.meeting_format,
                  ) && (
                    <NewTooltip
                      content={
                        {
                          telephone: 'Téléphone',
                          visio: 'Visio',
                          physique: 'Physique',
                          showroom: 'Showroom',
                        }[lead.follow_up_meeting.meeting_format]
                      }
                      placement="top"
                    >
                      <FontAwesomeIcon
                        icon={
                          {
                            telephone: faPhone,
                            visio: faVideo,
                            physique: faLocationDot,
                            showroom: faStore,
                          }[lead.follow_up_meeting.meeting_format]
                        }
                        size="sm"
                        style={{ marginLeft: 4 }}
                      />
                    </NewTooltip>
                  )}
                </Box>
              ))}
            {!lead.follow_up_meeting &&
              (lead.current_follow_up_status?.is_r1_completed ? (
                <Box sx={{ ml: 1 }}>R2 ou VT</Box>
              ) : (
                <Box sx={{ ml: 1 }}>R1</Box>
              ))}
          </Box>
        )}
      </TableCell>

      <TableCell sx={{ width: columnsWidth.action_result }}>
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <Typography sx={{ fontSize: 'inherit', textAlign: 'center' }}>
              {actionMapping[getAction(lead)] === 'Appel'
                ? `${actionMapping[getAction(lead)]} n°${
                    lead.follow_up_call.call_number
                  }`
                : actionMapping[getAction(lead)]}
            </Typography>
          </Grid>
          <Grid item>
            <Grid container spacing={1}>
              {getActionResult(lead).map((result) => (
                <Grid item key={result}>
                  <NewTooltip content={getActionResultTooltip(result)}>
                    <Button
                      variant="contained"
                      color={
                        isMuiColor(actionResultColors[result])
                          ? actionResultColors[result]
                          : undefined
                      }
                      style={
                        !isMuiColor(actionResultColors[result])
                          ? { backgroundColor: actionResultColors[result] }
                          : undefined
                      }
                      size="small"
                      onClick={() => actionResultOnClick[result](lead)}
                      sx={{
                        minWidth: 'auto',
                        width: '30px',
                        height: '30px',
                      }}
                    >
                      <FontAwesomeIcon
                        icon={actionResultIcons[result]}
                        size="xl"
                      />
                    </Button>
                  </NewTooltip>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </TableCell>

      {/* <TableCell sx={{ width: columnsWidth.action }}> */}
      {/*   <Grid container> */}
      {/*     <Typography sx={{ fontSize: 'inherit', textAlign: 'center' }}> */}
      {/*       {actionMapping[getAction(lead)] === 'Appel' */}
      {/*         ? `${actionMapping[getAction(lead)]} n°${ */}
      {/*             lead.follow_up_call.call_number */}
      {/*           }` */}
      {/*         : actionMapping[getAction(lead)]} */}
      {/*     </Typography> */}
      {/*   </Grid> */}
      {/* </TableCell> */}
      {/* <TableCell sx={{ width: columnsWidth.action_result }}> */}
      {/*   <Grid container spacing={1}> */}
      {/*     {getActionResult(lead).map((result) => ( */}
      {/*       <Grid item key={result}> */}
      {/*         /!* <NewTooltip content={actionResultMapping[result]}> *!/ */}
      {/*         <NewTooltip content={getActionResultTooltip(result)}> */}
      {/*           <Button */}
      {/*             variant="contained" */}
      {/*             // color={actionResultColors[result]} */}
      {/*             // style={{ backgroundColor: actionResultColors[result] }} */}
      {/*             color={ */}
      {/*               isMuiColor(actionResultColors[result]) */}
      {/*                 ? actionResultColors[result] */}
      {/*                 : undefined */}
      {/*             } */}
      {/*             style={ */}
      {/*               !isMuiColor(actionResultColors[result]) */}
      {/*                 ? { backgroundColor: actionResultColors[result] } */}
      {/*                 : undefined */}
      {/*             } */}
      {/*             size="small" */}
      {/*             onClick={() => actionResultOnClick[result](lead)} */}
      {/*             sx={{ */}
      {/*               minWidth: 'auto', */}
      {/*               width: '30px', */}
      {/*               height: '30px', */}
      {/*             }} */}
      {/*           > */}
      {/*             <FontAwesomeIcon icon={actionResultIcons[result]} size="xl" /> */}
      {/*           </Button> */}
      {/*         </NewTooltip> */}
      {/*       </Grid> */}
      {/*     ))} */}
      {/*   </Grid> */}
      {/* </TableCell> */}
      <TableCell sx={{ width: columnsWidth.delay }}>
        {lead.follow_up_task &&
        isDueDateReached(lead.follow_up_task.due_date) ? (
          <NewTooltip content="Vous êtes à temps et n'avez pas de retard sur le funnel de vente">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FontAwesomeIcon icon={faHourglassStart} />
              <Box sx={{ ml: 1 }}>0</Box>
            </Box>
          </NewTooltip>
        ) : (
          <NewTooltip
            content={`Vous avez ${getDaysOfDelay(
              lead.follow_up_task.due_date,
            )} jour${
              getDaysOfDelay(lead.follow_up_task.due_date) > 1 ? 's' : ''
            } de retard`}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FontAwesomeIcon
                icon={
                  getDaysOfDelay(lead.follow_up_task.due_date) >= 1
                    ? faAlarmExclamation
                    : faHourglassClock
                }
                color={
                  getDaysOfDelay(lead.follow_up_task.due_date) >= 1
                    ? 'red'
                    : 'orange'
                }
                size="xl"
              />
              <Box sx={{ ml: 1 }}>{`${getDaysOfDelay(
                lead.follow_up_task.due_date,
              )}`}</Box>
            </Box>
          </NewTooltip>
        )}
      </TableCell>
      <FollowUpDialog
        lead={lead}
        // disabled={loading}
        open={isFollowUpDialogOpen}
        onClose={() => {
          setIsFollowUpDialogOpen(false);
        }}
        onChange={() => onLeadChange(lead)}
        // isMeetingConfirmation
        section={followUpDialogSection}
      />
    </>
  );
}

ItemContent.propTypes = {
  index: PropTypes.number.isRequired,
  lead: PropTypes.shape().isRequired,
};

const MemoizedItemContent = React.memo(ItemContent);

function itemContent(index, lead) {
  return <MemoizedItemContent index={index} lead={lead} />;
}

function LeadFollowUpDashboard() {
  const savedStatusFilter = useMemo(
    () => localStorage.getItem('dashboard-lead-status-filter') || '0',
    [],
  );
  const savedPMFilter = useMemo(
    () =>
      CacheManager.instance
        .getModel('/users/')
        .get(localStorage.getItem('dashboard-project-manager-filter')) ||
      undefined,
    [],
  );
  const savedSetterFilter = useMemo(
    () =>
      CacheManager.instance
        .getModel('/users/')
        .get(localStorage.getItem('dashboard-setter-filter')) || undefined,
    [],
  );

  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [leads, setLeads] = useState([]);
  const [totalLeadsForBadges, setTotalLeadsForBadges] = useState([]);
  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [projectManager, setProjectManager] = useState(savedPMFilter);
  const [setter, setSetter] = useState(savedSetterFilter);
  const [countLeadByPm, setCountLeadByPm] = useState({ '-----': 0 });
  const initialStatusFilters = useMemo(() => {
    const raw =
      localStorage.getItem('dashboard-follow-up-status-filter') || '0';
    if (!raw || raw === '0' || raw === 'all') return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [raw];
    } catch {
      return [];
    }
  }, []);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [loadingCountByPm, setLoadingCountByPm] = useState(false);
  const [counts, setCounts] = useState(null);
  const [statusFilters, setStatusFilters] = useState(initialStatusFilters);
  const [leadCreationDateFilter, setLeadCreationDateFilter] = useState('');
  useCacheInvalidation(['leads-status', 'users']);
  const ref = useRef(null);
  const leadTableRef = useRef(null);
  const resizeObserver = useRef(null);
  const virtuoso = useRef(null);
  const prevLeads = usePrevious(leads);

  const { user } = useSelector(selectAuth);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const canSelectProjectManager = useMemo(
    () => user.is_admin_leader || user.is_external_accountant,
    [user],
  );
  const searchTimeout = useRef(null);
  const isInitialRenderSearch = useRef(true);
  const isInitialRenderProjectManager = useRef(true);
  const isInitialRenderSetter = useRef(true);
  const isInitialRenderFilters = useRef(true);
  const isInitialRenderStatusFilters = useRef(true);
  const isInitialRenderLeadCreationDate = useRef(true);
  const isInitialRenderOffset = useRef(true);

  /**
   * Handles search change
   * @param event {Event} Event
   */
  const handleSearchChange = (event) => {
    const { target } = event;
    const { name, value } = target;
    setSearch(value);
  };

  /**
   * Handles project manager change
   * @param _projectManager {Object} Project manager
   */
  const handleProjectManagerChange = (_projectManager) => {
    setProjectManager(_projectManager);
  };

  /**
   * Handles setterchange
   * @param _setter {Object} Project manager
   */
  const handleSetterChange = (_setter) => {
    setSetter(_setter);
  };

  /**
   * Handles sorting change
   * @param _sorting {Object} Sorting
   */
  const handleSortingChange = (_sorting) => {
    let newSorting = _sorting;
    if (sorting.startsWith('-')) {
      if (_sorting === sorting.replace('-', '')) {
        newSorting = '';
      }
    } else if (_sorting === sorting) {
      newSorting = `-${sorting}`;
    }
    setSorting(newSorting);
  };

  /**
   * Loads stats from the API
   */
  const loadStats = () => {
    setLoadingCountByPm(true);
    return api
      .get('leads/get_count_leads_by_pm/')
      .then((result) => {
        setCountLeadByPm(result.data);
        setLoadingCountByPm(false);
      })
      .catch((error) => {
        console.error(error);
        setLoadingCountByPm(false);
      });
  };

  /**
   * Clears search
   */
  const clearSearch = () => {
    setSearch('');
  };

  const handleStatusFilterClick = (status, event) => {
    const multi = event?.ctrlKey || event?.metaKey;
    let next;
    if (multi) {
      const idx = statusFilters.indexOf(status);
      if (idx === -1) next = [...statusFilters, status];
      else if (statusFilters.length <= 1) next = [];
      else next = statusFilters.filter((_, i) => i !== idx);
    } else {
      next =
        statusFilters.length === 1 && statusFilters[0] === status
          ? []
          : [status];
    }
    setStatusFilters(next);
    localStorage.setItem(
      'dashboard-follow-up-status-filter',
      next.length ? JSON.stringify(next) : 'all',
    );
  };

  /**
   * Loads lead counts from the API
   * @returns {Promise<T>}
   */
  const loadCounts = () => {
    setLoadingCounts(true);
    const params = new URLSearchParams();
    if (projectManager) params.set('project_manager', projectManager.id);
    params.set('is_follow_up', 'true');
    if (leadCreationDateFilter && leadCreationDateFilter !== '') {
      params.set('creation_date_gt', leadCreationDateFilter);
    }
    return api
      .get(`leads/counts?${params.toString()}`)
      .then((result) => {
        setCounts(result.data);
        setLoadingCounts(false);
      })
      .catch((err) => {
        console.error(err);
        setCounts(null);
        setLoadingCounts(false);
      });
  };

  /**
   * Loads leads from the API
   * @returns {Promise<T>}
   */
  const loadLeads = (reset) => {
    let tmpSearch = search;
    setLoading(true);
    const r1 = /^0[1-9]/;
    if (search.match(r1)) {
      const r2 = /^0/;
      tmpSearch = tmpSearch.replace(r2, '+33');
    }
    const statusFiltersParam =
      statusFilters.length > 0
        ? `&${statusFilters
            .map((s) => `status_type=${encodeURIComponent(s)}`)
            .join('&')}`
        : '';
    return api
      .get(
        `leads/?filter=True&search=${tmpSearch.split(' ').join(',')}${
          sorting && sorting.length > 0 ? `&ordering=${sorting}` : ''
        }&offset=${reset ? 0 : offset}&limit=50${
          projectManager ? `&project_manager=${projectManager.id}` : ''
        }${setter ? `&setter=${setter.id}` : ''}${
          tmpSearch === '' ? '&is_external_referrer=false' : ''
        }&is_test=false&is_follow_up=true${statusFiltersParam}${
          leadCreationDateFilter && leadCreationDateFilter !== ''
            ? `&lead_creation_date=${leadCreationDateFilter}`
            : ''
        }`,
      )
      .then((result) => {
        const filteredLeads = filterLeadsByScheduledOrDueDate(
          result.data.results,
        );
        if (offset && offset > 0 && !reset) {
          setLeads((prevState) => [...prevState, ...filteredLeads]);
        } else {
          setLeads(filteredLeads);
        }
        setTotalRows(filteredLeads.length);
        setTotalLeadsForBadges(result.data.results);
        setLoading(false);
        return filteredLeads;
      })
      .catch((err) => {
        console.error(err);
        const { message } = parseFieldError(err);
        if (message) enqueueSnackbar(message, { variant: 'error' });
        setLoading(false);
      });
  };
  /**
   * Loads a specific lead from the API
   * @param id {Number} Lead ID
   * @returns {Promise<T>}
   */
  const loadLead = (id) =>
    api
      .get(`leads/${id}/`)
      .then((result) => result.data)
      .catch((err) => {
        console.error(err);
        const { message } = parseFieldError(err);
        if (message) enqueueSnackbar(message, { variant: 'error' });
        setLoading(false);
      });

  /**
   * Handles lead input change
   * @param lead {Object} Lead
   */
  const onLeadInputChange = (lead) => {
    loadLead(lead.id)
      .then((newLead) => {
        setLeads((prevState) => {
          const newLeads = [...prevState];
          const indexClient = newLeads.find((o) => o.id === newLead.id);
          const index = newLeads.indexOf(indexClient);
          newLeads[index] = newLead;
          return newLeads;
        });
      })
      .catch((err) => {
        console.error(err);
        const { message } = parseFieldError(err);
        if (message) enqueueSnackbar(message, { variant: 'error' });
        setLoading(false);
      });
  };

  const onTableEndReached = (n) => {
    if (offset + 50 < totalRows) {
      setOffset(offset + 50);
    }
  };

  const reloadAllData = () => {
    Promise.all([loadLeads(true), loadCounts(), loadStats()])
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  const dashboardContextData = useMemo(
    () => ({
      projectManager,
      setter,
      sorting,
      handleSortingChange,
      onLeadChange: onLeadInputChange,
      reloadAllData,
      totalRows,
    }),
    [projectManager, sorting, totalRows, setter],
  );

  const resizeTable = (el) => {
    if (el) {
      const topHeight = el.getBoundingClientRect().top;
      const totalHeight =
        window.screen.availHeight - (window.outerHeight - window.innerHeight);
      const height = totalHeight - topHeight - 20;
      ref.current.style.height = `${height}px`;
    }
  };

  useEffect(() => {
    Promise.all([loadLeads(), loadCounts(), loadStats()])
      .then(() => {
        resizeTable(leadTableRef.current);
        resizeObserver.current = new ResizeObserver((entries) => {
          if (entries.length > 0) {
            resizeTable(leadTableRef.current);
          }
        });
        resizeObserver.current.observe(document.getElementById('root'));
      })
      .catch((err) => {
        console.error(err);
      });
    return () => {
      if (resizeObserver && resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (isInitialRenderSearch.current) {
      isInitialRenderSearch.current = false;
      return;
    }
    clearTimeout(searchTimeout.current);
    if (search.length > 2 || search.length === 0) {
      searchTimeout.current = setTimeout(() => {
        setLoading(true);
        loadLeads(true);
      }, 500);
    }
  }, [search]);

  useEffect(() => {
    if (isInitialRenderProjectManager.current) {
      isInitialRenderProjectManager.current = false;
      return;
    }
    Promise.all([
      localStorage.setItem(
        'dashboard-project-manager-filter',
        projectManager ? projectManager.id : '',
      ),
      loadLeads(true),
      loadCounts(),
      loadStats(),
    ]);
  }, [projectManager]);

  useEffect(() => {
    if (isInitialRenderSetter.current) {
      isInitialRenderSetter.current = false;
      return;
    }
    Promise.all([
      localStorage.setItem('dashboard-setter-filter', setter ? setter.id : ''),
      loadLeads(true),
      loadCounts(),
      loadStats(),
    ]);
  }, [setter]);

  useEffect(() => {
    if (isInitialRenderFilters.current) {
      isInitialRenderFilters.current = false;
      return;
    }
    loadLeads(true);
  }, [sorting]);

  useEffect(() => {
    if (isInitialRenderStatusFilters.current) {
      isInitialRenderStatusFilters.current = false;
      return;
    }
    loadLeads(true);
  }, [statusFilters]);

  useEffect(() => {
    if (isInitialRenderLeadCreationDate.current) {
      isInitialRenderLeadCreationDate.current = false;
      return;
    }
    if (
      leadCreationDateFilter &&
      leadCreationDateFilter !== '' &&
      !moment(leadCreationDateFilter, 'YYYY-MM-DD', true).isValid()
    ) {
      return;
    }
    Promise.all([loadLeads(true), loadCounts()]);
  }, [leadCreationDateFilter]);

  useEffect(() => {
    if (isInitialRenderOffset.current) {
      isInitialRenderOffset.current = false;
      return;
    }
    loadLeads();
  }, [offset]);

  useEffect(() => {
    if (
      prevLeads &&
      prevLeads.current &&
      leads.length !== prevLeads.current.length &&
      leads.length > 0 &&
      prevLeads.current.length > 0 &&
      leads[0].id !== prevLeads.current[0].id
    ) {
      // eslint-disable-next-line no-console
      virtuoso.current.scrollToIndex({
        index: 0,
        align: 'start',
        behavior: 'auto',
      });
    }
  }, [leads]);

  return (
    <ThemeProvider>
      <Box mx={2} mb={2} id="lead-dashboard">
        <Box mb={2}>
          <Grid container justifyContent="center" spacing={1}>
            <Grid
              item
              xs={user.is_admin_leader || user.is_external_accountant ? 7 : 12}
            >
              <BoxLoader loading={loadingCounts}>
                <Paper
                  sx={{
                    bgcolor: 'primary.lighter',
                    px: 2,
                    pt: 4,
                    pb: 2,
                    position: 'relative',
                  }}
                >
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'inline-block',
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      fontSize: 10,
                      px: 1,
                      py: 0.3,
                      borderBottomRightRadius: 5,
                      borderBottomLeftRadius: 5,
                      zIndex: 1000,
                    }}
                  >
                    Statistiques leads
                    {projectManager && (
                      <>
                        {' '}
                        pour {projectManager.first_name}{' '}
                        {projectManager.last_name}
                      </>
                    )}
                  </Typography>
                  <Grid container justifyContent="space-evenly">
                    <Grid
                      item
                      onClick={(e) => handleStatusFilterClick('waiting', e)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <NewTooltip content="En attente">
                        <Badge
                          badgeContent={counts ? counts.waiting : 0}
                          overlap="rectangular"
                          color="primary"
                          max={99999}
                          showZero
                        >
                          <Avatar
                            sx={
                              !statusFilters.includes('waiting')
                                ? {
                                    bgcolor: 'white',
                                    color: 'primary.dark',
                                    border: '1px solid',
                                    borderColor: 'primary.dark',
                                  }
                                : {
                                    bgcolor: 'primary.dark',
                                    color: 'white',
                                  }
                            }
                          >
                            <FontAwesomeIcon icon={faHourglassClock} />
                          </Avatar>
                        </Badge>
                      </NewTooltip>
                    </Grid>
                    <Grid
                      item
                      onClick={(e) =>
                        handleStatusFilterClick('no_communication_tried', e)
                      }
                      sx={{ cursor: 'pointer' }}
                    >
                      <NewTooltip content="0 tentative de communication">
                        <Badge
                          badgeContent={
                            counts ? counts.no_communication_tried : 0
                          }
                          overlap="rectangular"
                          color="error"
                          max={99999}
                          showZero
                        >
                          <Avatar
                            sx={
                              !statusFilters.includes('no_communication_tried')
                                ? {
                                    bgcolor: 'white',
                                    color: 'primary.dark',
                                    border: '1px solid',
                                    borderColor: 'primary.dark',
                                  }
                                : {
                                    bgcolor: 'primary.dark',
                                    color: 'white',
                                  }
                            }
                          >
                            <FontAwesomeIcon icon={faPhoneSlash} />
                          </Avatar>
                        </Badge>
                      </NewTooltip>
                    </Grid>
                    <Grid
                      item
                      onClick={(e) =>
                        handleStatusFilterClick(
                          'at_least_one_communication_tried',
                          e,
                        )
                      }
                      sx={{ cursor: 'pointer' }}
                    >
                      <NewTooltip content="À contacter">
                        <Badge
                          badgeContent={
                            counts ? counts.at_least_one_communication_tried : 0
                          }
                          overlap="rectangular"
                          color="error"
                          max={99999}
                          showZero
                        >
                          <Avatar
                            sx={
                              !statusFilters.includes(
                                'at_least_one_communication_tried',
                              )
                                ? {
                                    bgcolor: 'white',
                                    color: 'primary.dark',
                                    border: '1px solid',
                                    borderColor: 'primary.dark',
                                  }
                                : {
                                    bgcolor: 'primary.dark',
                                    color: 'white',
                                  }
                            }
                          >
                            <FontAwesomeIcon icon={faPhoneArrowUpRight} />
                          </Avatar>
                        </Badge>
                      </NewTooltip>
                    </Grid>
                    <Grid
                      item
                      onClick={(e) =>
                        handleStatusFilterClick('quote_to_be_sent', e)
                      }
                      sx={{ cursor: 'pointer' }}
                    >
                      <NewTooltip content="Devis à effectuer">
                        <Badge
                          badgeContent={counts ? counts.quote_to_be_sent : 0}
                          overlap="rectangular"
                          color="error"
                          max={99999}
                          showZero
                        >
                          <Avatar
                            sx={
                              !statusFilters.includes('quote_to_be_sent')
                                ? {
                                    bgcolor: 'white',
                                    color: 'primary.dark',
                                    border: '1px solid',
                                    borderColor: 'primary.dark',
                                  }
                                : {
                                    bgcolor: 'primary.dark',
                                    color: 'white',
                                  }
                            }
                          >
                            <FontAwesomeIcon icon={faPaperPlane} />
                          </Avatar>
                        </Badge>
                      </NewTooltip>
                    </Grid>
                    <Grid
                      item
                      onClick={(e) => handleStatusFilterClick('offers_sent', e)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <NewTooltip content="Devis en cours">
                        <Badge
                          badgeContent={counts ? counts.offers_sent : 0}
                          overlap="rectangular"
                          color="primary"
                          max={99999}
                          showZero
                        >
                          <Avatar
                            sx={
                              !statusFilters.includes('offers_sent')
                                ? {
                                    bgcolor: 'white',
                                    color: 'primary.dark',
                                    border: '1px solid',
                                    borderColor: 'primary.dark',
                                  }
                                : {
                                    bgcolor: 'primary.dark',
                                    color: 'white',
                                  }
                            }
                          >
                            <FontAwesomeIcon icon={faEnvelopeCircleCheck} />
                          </Avatar>
                        </Badge>
                      </NewTooltip>
                    </Grid>
                    <Grid
                      item
                      onClick={(e) => handleStatusFilterClick('in_progress', e)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <NewTooltip content="En cours">
                        <Badge
                          badgeContent={
                            counts
                              ? counts.waiting +
                                counts.at_least_one_communication_tried +
                                counts.quote_to_be_sent +
                                counts.offers_sent
                              : 0
                          }
                          color="primary"
                          max={99999}
                          showZero
                        >
                          <Avatar
                            sx={
                              !statusFilters.includes('in_progress')
                                ? {
                                    bgcolor: 'white',
                                    color: 'primary.dark',
                                    border: '1px solid',
                                    borderColor: 'primary.dark',
                                  }
                                : {
                                    bgcolor: 'primary.dark',
                                    color: 'white',
                                  }
                            }
                          >
                            <FontAwesomeIcon icon={faArrowProgress} />
                          </Avatar>
                        </Badge>
                      </NewTooltip>
                    </Grid>
                  </Grid>
                </Paper>
              </BoxLoader>
            </Grid>
            {(user.is_admin_leader || user.is_external_accountant) && (
              <Grid item xs={5}>
                <BoxLoader loading={loadingCountByPm}>
                  <Paper
                    sx={{
                      bgcolor: 'secondary.lighter',
                      px: 2,
                      pt: 4,
                      pb: 1,
                      position: 'relative',
                    }}
                  >
                    <Typography
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'inline-block',
                        bgcolor: 'secondary.light',
                        color: 'secondary.contrastText',
                        fontSize: 10,
                        px: 1,
                        py: 0.3,
                        borderBottomRightRadius: 5,
                        borderBottomLeftRadius: 5,
                        zIndex: 1000,
                      }}
                    >
                      ce mois-ci
                    </Typography>
                    <Grid container justifyContent="space-evenly">
                      {Object.entries(countLeadByPm).map(([key, value]) => (
                        <Grid item key={key}>
                          <NewTooltip content={value.full_name}>
                            <Badge
                              badgeContent={value.count}
                              overlap="rectangular"
                              color="secondary"
                              max={999}
                              showZero
                            >
                              {value.has_avatar ? (
                                <Avatar src={`${getApiUrl()}/avatar/${key}`} />
                              ) : (
                                <Avatar {...stringAvatar(value.full_name)} />
                              )}
                            </Badge>
                          </NewTooltip>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </BoxLoader>
              </Grid>
            )}
          </Grid>
        </Box>

        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          {/* <Grid item xs={(user.is_admin_leader ||
               user.is_external_accountant) ? 4 : 12}> */}
          <Grid item xs={2}>
            <TextField
              variant="outlined"
              fullWidth
              id="search-client"
              label="Rechercher un lead (nom, ID, CP ou tel, 3 caractères min.)"
              name="search"
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment:
                  search !== '' ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={clearSearch}
                        onMouseDown={clearSearch}
                        edge="end"
                        size="large"
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={1.6}>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <DatePicker
                label="Création lead"
                format="DD/MM/YYYY"
                value={
                  leadCreationDateFilter ? moment(leadCreationDateFilter) : null
                }
                onChange={(d) =>
                  setLeadCreationDateFilter(
                    d ? moment(d).format('YYYY-MM-DD') : '',
                  )
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  right: 38,
                  bottom: 12,
                  top: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Box
                  sx={{
                    pointerEvents:
                      leadCreationDateFilter !== '' ? 'auto' : 'none',
                    visibility:
                      leadCreationDateFilter !== '' ? 'visible' : 'hidden',
                  }}
                >
                  <IconButton
                    onClick={() => setLeadCreationDateFilter('')}
                    onMouseDown={(e) => e.preventDefault()}
                    size="small"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs>
            <Grid container justifyContent="center">
              <NewTooltip
                content={
                  <>
                    <Typography variant="body2">
                      <strong>R1</strong> : RDV téléphonique pour qualifications
                    </Typography>
                    <Typography variant="body2">
                      <strong>R2</strong> : Optionnel RDV téléphonique pré-VT
                    </Typography>
                    <Typography variant="body2">
                      <strong>VT</strong> : VT physique
                    </Typography>
                    <Typography variant="body2">
                      <strong>R3</strong> : RDV si pas de signature à la VT
                    </Typography>
                    <Typography variant="body2" align="center" sx={{ m: 1 }}>
                      <hr />
                    </Typography>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            bgcolor: '#FF0000',
                            borderRadius: '50%',
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="body2">Leads en retard</Typography>
                      </Grid>
                    </Grid>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            bgcolor: 'yellow',
                            borderRadius: '50%',
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="body2">Leads du jour</Typography>
                      </Grid>
                    </Grid>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            bgcolor: 'green',
                            borderRadius: '50%',
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="body2">
                          Leads à traiter dans la semaine
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                }
                arrow
              >
                <FontAwesomeIcon icon={faCircleInfo} size="xl" color="green" />
              </NewTooltip>
              <Typography variant="body2" sx={{ ml: 0.8 }}>
                Informations
              </Typography>
            </Grid>
          </Grid>
          {canSelectProjectManager && (
            <Grid item xs={1.5}>
              <CustomAutocomplete
                name="projectManager"
                label="Chargé de projet"
                value={projectManager}
                endpoint={(input) =>
                  `/users/?search=${
                    input === 0 || input === undefined ? '' : input
                  }&is_project_manager=True`
                }
                getOptionLabel={(o) => o.full_name || ''}
                onChange={(e, v) => handleProjectManagerChange(v)}
                allowNull
              />
            </Grid>
          )}
          {canSelectProjectManager && (
            <Grid item xs={1.5}>
              <CustomAutocomplete
                name="setter"
                label="Setteur"
                value={setter}
                endpoint={(input) =>
                  `/users/?search=${
                    input === 0 || input === undefined ? '' : input
                  }&is_setter=True`
                }
                getOptionLabel={(o) => o.full_name || ''}
                onChange={(e, v) => handleSetterChange(v)}
                allowNull
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <BoxLoader loading={loading}>
        <Box mx={2}>
          <Paper
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              px: 2,
              py: 1,
              fontWeight: 'medium',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                textAlign: 'right',
              }}
            >
              {totalRows} leads affichés
            </Typography>
          </Paper>
        </Box>
        <Box mx={2} id="lead-table" ref={leadTableRef}>
          <DashboardContext.Provider value={dashboardContextData}>
            <Paper
              sx={{
                width: '100%',
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
              }}
              ref={ref}
            >
              <TableVirtuoso
                data={leads}
                components={TableComponents}
                fixedHeaderContent={fixedHeaderContent}
                itemContent={itemContent}
                overscan={{ main: 50, reverse: 50 }}
                fixedItemHeight={80}
                endReached={onTableEndReached}
                ref={virtuoso}
              />
            </Paper>
          </DashboardContext.Provider>
        </Box>
      </BoxLoader>
    </ThemeProvider>
  );
}

export default LeadFollowUpDashboard;
